import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { RewardsPreferenceFacade } from '@features/rewards-preference/facades/rewards-preference/rewards-preference.facade';
import { RewardsPreference } from '@features/rewards-preference/types/rewards-preference.type';
import { Store } from '@ngrx/store';
import { Breakpoints } from '@rd-lib/breakpoints';
import { getAuthIsLoggedIn } from '@rd/core/auth/store/selectors/auth.selectors';
import {
  AssetService,
  AssetType,
} from '@rd/core/services/asset-service/asset.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { WindowResizeService } from '@rd/core/services/window-resize/window-resize.service';
import { CoreState } from '@rd/core/store/reducers';
import { LoyaltyProgram } from '@rd/types';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

interface CommonIconType {
  name: string;
  imageUrl: string;
}

@Component({
  selector: 'star-core-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StarHomePageComponent implements OnInit, OnDestroy {
  saBenefits: CommonIconType[] = [
    {
      name: 'Priority Airport Check&#8209;in',
      imageUrl: this.assetService.getAssetPath(
        'benefit_priority_airport_check-in.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Priority Baggage Handling',
      imageUrl: this.assetService.getAssetPath(
        'benefit_priority_baggae_handling.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Airport Lounge Access',
      imageUrl: this.assetService.getAssetPath(
        'benefit_airport_lounge_access.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Priority Boarding',
      imageUrl: this.assetService.getAssetPath(
        'benefit_priority_boarding.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Extra Baggage Allowance',
      imageUrl: this.assetService.getAssetPath(
        'benefit_extra_baggage_allowance.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Gold Track',
      imageUrl: this.assetService.getAssetPath(
        'benefit_gold_track.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Priority Reservations Waitlist',
      imageUrl: this.assetService.getAssetPath(
        'benefit_priority_reservations_waitlist.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Priority Airport Stand&#8209;by',
      imageUrl: this.assetService.getAssetPath(
        'benefit_priority_airport_stand-by.png',
        AssetType.Image,
      ),
    },
  ];

  saMemberAirlines: CommonIconType[] = [
    {
      name: 'Air Canada',
      imageUrl: this.assetService.getAssetPath(
        'brand_air_canada.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Air New Zealand',
      imageUrl: this.assetService.getAssetPath(
        'brand_air_new_zealand.png',
        AssetType.Image,
      ),
    },
    {
      name: 'EVA Air',
      imageUrl: this.assetService.getAssetPath(
        'brand_eva_air.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Singapore Airlines',
      imageUrl: this.assetService.getAssetPath(
        'brand_singapore_airlines.png',
        AssetType.Image,
      ),
    },
    {
      name: 'South African Airways',
      imageUrl: this.assetService.getAssetPath(
        'brand_south_african_airway.png',
        AssetType.Image,
      ),
    },
    {
      name: 'Thai Airways',
      imageUrl: this.assetService.getAssetPath(
        'brand_thai_airways.png',
        AssetType.Image,
      ),
    },
    {
      name: 'United Airlines',
      imageUrl: this.assetService.getAssetPath(
        'brand_united_airlines.png',
        AssetType.Image,
      ),
    },
  ];

  isLoggedIn$ = this.store.select(getAuthIsLoggedIn);
  rewardsPreference$: Observable<RewardsPreference | null> =
    this.rewardsPreferenceFacade.rewardsPreference$;
  rewardsPreferenceCreateSuccess: Observable<boolean> =
    this.rewardsPreferenceFacade.getCreateRewardsPreferenceSuccess$;

  rewardsPreferenceLoyaltyProgram$: Observable<LoyaltyProgram | null> =
    this.rewardsPreferenceFacade.rewardsPreference$.pipe(
      switchMap((rp) => {
        if (rp === null) {
          return of(null);
        }
        return this.loyaltyProgramFacade.loyaltyProgramsMap$.pipe(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          map((a) => a[rp!.membership.loyaltyProgramId]),
        );
      }),
      map((value) => {
        if (!value) {
          return null;
        }

        return value as LoyaltyProgram;
      }),
    );

  introductionImages = {
    earnPointsUrl: this.assetService.getAssetPath(
      'rd-earn-points.jpeg',
      AssetType.Image,
    ),
    earnPointsMobileUrl: this.assetService.getAssetPath(
      'rd-earn-points-mobile.jpeg',
      AssetType.Image,
    ),
    redeemPointsUrl: this.assetService.getAssetPath(
      'rd-use-points.jpeg',
      AssetType.Image,
    ),
    redeemPointsMobileUrl: this.assetService.getAssetPath(
      'rd-use-points-mobile.jpeg',
      AssetType.Image,
    ),
  };

  destroy$ = new Subject<boolean>();
  isMobileViewport: boolean;

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<{ core: CoreState }>,
    private windowResizeService: WindowResizeService,
    private rewardsPreferenceFacade: RewardsPreferenceFacade,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
    private pageTitleService: PageTitleService,
    private assetService: AssetService,
  ) {}

  ngOnInit(): void {
    this.detectViewportSize();
    this.pageTitleService.setPageTitle('home-page');
    this.windowResizeService.windowResizeObservable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.detectViewportSize();
        this.cdRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private detectViewportSize(): void {
    this.isMobileViewport = window.innerWidth < Breakpoints.DESKTOP;
  }
}
